import React from 'react';

/**
 * EmptyResumesIllustration: A clean, modern SVG illustration for the "No Resumes Yet" empty state.
 * Fully scalable, hardware-accelerated, and theme-matched to indigo/slate dark mode.
 */
export function EmptyResumesIllustration({ className = "w-48 h-48 mx-auto" }) {
  return (
    <svg
      viewBox="0 0 240 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="bgGlow" x1="120" y1="20" x2="120" y2="220" gradientUnits="userSpaceOnUse">
          <stop stopColor="#6366f1" stopOpacity="0.25" />
          <stop offset="1" stopColor="#38bdf8" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="docGrad" x1="60" y1="40" x2="180" y2="200" gradientUnits="userSpaceOnUse">
          <stop stopColor="#1e293b" />
          <stop offset="1" stopColor="#0f172a" />
        </linearGradient>
        <linearGradient id="accentGrad" x1="0" y1="0" x2="1" y2="1">
          <stop stopColor="#6366f1" />
          <stop offset="1" stopColor="#818cf8" />
        </linearGradient>
        <linearGradient id="sparkleGrad" x1="0" y1="0" x2="1" y2="1">
          <stop stopColor="#38bdf8" />
          <stop offset="1" stopColor="#818cf8" />
        </linearGradient>
        <filter id="softShadow" x="40" y="30" width="160" height="180" filterUnits="userSpaceOnUse">
          <feDropShadow dx="0" dy="12" stdDeviation="16" floodColor="#4f46e5" floodOpacity="0.25" />
        </filter>
      </defs>

      {/* Ambient background glow circle */}
      <circle cx="120" cy="120" r="90" fill="url(#bgGlow)" />

      {/* Decorative floating dots/rings */}
      <circle cx="45" cy="80" r="4" fill="#818cf8" fillOpacity="0.4" />
      <circle cx="195" cy="70" r="3" fill="#38bdf8" fillOpacity="0.5" />
      <circle cx="190" cy="170" r="5" fill="#6366f1" fillOpacity="0.3" />
      <circle cx="50" cy="165" r="3.5" fill="#38bdf8" fillOpacity="0.4" />

      {/* Main Resume Sheet */}
      <g filter="url(#softShadow)">
        <rect
          x="65"
          y="45"
          width="110"
          height="145"
          rx="12"
          fill="url(#docGrad)"
          stroke="#334155"
          strokeWidth="1.5"
        />
        {/* Document Fold Accent at top right */}
        <path
          d="M155 45L175 65H161C157.686 65 155 62.3137 155 59V45Z"
          fill="#334155"
        />

        {/* Profile Avatar Placeholder on Resume */}
        <circle cx="90" cy="75" r="12" fill="#334155" />
        <circle cx="90" cy="73" r="5" fill="#64748b" />
        <path d="M82 84C82 80 86 78 90 78C94 78 98 80 98 84" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" />

        {/* Candidate Name & Title lines */}
        <rect x="110" y="68" width="50" height="5" rx="2.5" fill="url(#accentGrad)" />
        <rect x="110" y="78" width="35" height="3" rx="1.5" fill="#64748b" />

        {/* Horizontal Divider */}
        <line x1="77" y1="96" x2="163" y2="96" stroke="#1e293b" strokeWidth="1.5" />

        {/* Section 1: Experience Lines */}
        <rect x="77" y="106" width="32" height="4" rx="2" fill="#818cf8" fillOpacity="0.8" />
        <rect x="77" y="115" width="86" height="3" rx="1.5" fill="#475569" />
        <rect x="77" y="122" width="76" height="3" rx="1.5" fill="#334155" />
        <rect x="77" y="129" width="60" height="3" rx="1.5" fill="#334155" />

        {/* Section 2: Skills Badges */}
        <rect x="77" y="142" width="28" height="4" rx="2" fill="#818cf8" fillOpacity="0.8" />
        <rect x="77" y="152" width="22" height="8" rx="4" fill="#312e81" stroke="#4f46e5" strokeWidth="0.8" />
        <rect x="103" y="152" width="26" height="8" rx="4" fill="#312e81" stroke="#4f46e5" strokeWidth="0.8" />
        <rect x="133" y="152" width="20" height="8" rx="4" fill="#312e81" stroke="#4f46e5" strokeWidth="0.8" />

        {/* Floating Verified Badge Icon */}
        <g transform="translate(145, 150)">
          <circle cx="16" cy="16" r="16" fill="#4f46e5" />
          <path
            d="M11 16.5L14.5 20L21 13"
            stroke="#ffffff"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      </g>

      {/* Floating Sparkle Stars */}
      <path
        d="M60 40L62 46L68 48L62 50L60 56L58 50L52 48L58 46L60 40Z"
        fill="url(#sparkleGrad)"
      />
      <path
        d="M185 110L186.5 114.5L191 116L186.5 117.5L185 122L183.5 117.5L179 116L183.5 114.5L185 110Z"
        fill="url(#sparkleGrad)"
      />
    </svg>
  );
}

/**
 * PreviewErrorIllustration: Clean graphic shown when PDF or template preview fails to load
 */
export function PreviewErrorIllustration({ className = "w-36 h-36 mx-auto" }) {
  return (
    <svg
      viewBox="0 0 160 160"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="errGlow" x1="80" y1="10" x2="80" y2="150" gradientUnits="userSpaceOnUse">
          <stop stopColor="#f43f5e" stopOpacity="0.2" />
          <stop offset="1" stopColor="#6366f1" stopOpacity="0" />
        </linearGradient>
      </defs>
      <circle cx="80" cy="80" r="60" fill="url(#errGlow)" />
      
      {/* Broken document outline */}
      <rect x="45" y="35" width="70" height="90" rx="8" fill="#1e293b" stroke="#475569" strokeWidth="1.5" />
      <line x1="55" y1="55" x2="105" y2="55" stroke="#334155" strokeWidth="2" strokeLinecap="round" />
      <line x1="55" y1="68" x2="95" y2="68" stroke="#334155" strokeWidth="2" strokeLinecap="round" />
      <line x1="55" y1="81" x2="85" y2="81" stroke="#334155" strokeWidth="2" strokeLinecap="round" />

      {/* Warning badge */}
      <circle cx="102" cy="105" r="18" fill="#e11d48" />
      <path
        d="M102 96V106M102 112H102.01"
        stroke="#ffffff"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * StepGraphic: Polished SVG iconography/illustrations for the 4-step onboarding flow.
 */
export function StepGraphic({ step = 1, className = "w-8 h-8" }) {
  switch (step) {
    case 1:
      // Upload Resume: Clean file with arrow
      return (
        <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
          <rect x="6" y="4" width="20" height="24" rx="4" fill="#312e81" stroke="#6366f1" strokeWidth="1.5" />
          <path d="M12 9H20" stroke="#a5b4fc" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M12 13H17" stroke="#a5b4fc" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="16" cy="20" r="5" fill="#4f46e5" />
          <path d="M16 22V18M16 18L14 20M16 18L18 20" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 2:
      // Target JD: Target bullseye with sparkles
      return (
        <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
          <circle cx="16" cy="16" r="11" fill="#312e81" stroke="#6366f1" strokeWidth="1.5" />
          <circle cx="16" cy="16" r="6" fill="#1e1b4b" stroke="#818cf8" strokeWidth="1.5" />
          <circle cx="16" cy="16" r="2.5" fill="#38bdf8" />
          <path d="M25 7L26 9L28 10L26 11L25 13L24 11L22 10L24 9L25 7Z" fill="#38bdf8" />
        </svg>
      );
    case 3:
      // Choose Template: Grid layout selector
      return (
        <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
          <rect x="5" y="5" width="22" height="22" rx="4" fill="#312e81" stroke="#6366f1" strokeWidth="1.5" />
          <rect x="8" y="8" width="6" height="16" rx="2" fill="#4f46e5" />
          <rect x="16" y="8" width="8" height="4" rx="1.5" fill="#818cf8" />
          <rect x="16" y="14" width="8" height="4" rx="1.5" fill="#a5b4fc" />
          <rect x="16" y="20" width="8" height="4" rx="1.5" fill="#a5b4fc" />
        </svg>
      );
    case 4:
      // Download PDF: Download card with check badge
      return (
        <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
          <rect x="6" y="5" width="20" height="22" rx="4" fill="#064e3b" stroke="#10b981" strokeWidth="1.5" />
          <path d="M16 11V18M16 18L13 15M16 18L19 15" stroke="#a7f3d0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M11 21H21" stroke="#a7f3d0" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="23" cy="22" r="4.5" fill="#10b981" />
          <path d="M21.5 22L22.5 23L24.5 21" stroke="#ffffff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    default:
      return null;
  }
}
